import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Box, Divider,
  Switch, FormControlLabel, Tabs, Tab,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface SMTPConfig {
  id: number;
  tenant_id: number;
  host: string;
  port: number;
  username: string | null;
  use_tls: boolean;
  from_email: string;
  from_name: string;
  is_active: boolean;
}

interface Contact {
  id: number;
  tenant_id: number;
  name: string;
  email: string;
  escalation_tier: number;
  is_active: boolean;
}

interface Rule {
  id: number;
  tenant_id: number;
  name: string;
  event_type: string;
  threshold_seconds: number;
  escalation_tier: number;
  send_recovery: boolean;
  digest_mode: boolean;
  is_active: boolean;
}

interface LogEntry {
  id: number;
  event_type: string;
  recipient: string;
  subject: string;
  status: string;
  created_at: string;
}

export default function NotificationsPage() {
  const { tenantId } = useAuth();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // SMTP state
  const [smtp, setSmtp] = useState<SMTPConfig | null>(null);
  const [smtpForm, setSmtpForm] = useState({
    host: '', port: '587', username: '', password: '', use_tls: true,
    from_email: '', from_name: 'WatchCenter Station Alert', is_active: true,
  });

  // Contacts state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactDialog, setContactDialog] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTier, setContactTier] = useState('1');

  // Rules state
  const [rules, setRules] = useState<Rule[]>([]);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    name: '', event_type: 'agent_offline', threshold_seconds: '300',
    escalation_tier: '1', send_recovery: true, digest_mode: false,
  });

  // Log state
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const loadAll = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [smtpRes, contactsRes, rulesRes, logsRes] = await Promise.all([
        api.get(`/api/tenants/${tenantId}/notifications/smtp`),
        api.get(`/api/tenants/${tenantId}/notifications/contacts`),
        api.get(`/api/tenants/${tenantId}/notifications/rules`),
        api.get(`/api/tenants/${tenantId}/notifications/log`),
      ]);
      if (smtpRes.data) {
        setSmtp(smtpRes.data);
        setSmtpForm({
          host: smtpRes.data.host, port: smtpRes.data.port.toString(),
          username: smtpRes.data.username || '', password: '',
          use_tls: smtpRes.data.use_tls, from_email: smtpRes.data.from_email,
          from_name: smtpRes.data.from_name, is_active: smtpRes.data.is_active,
        });
      }
      setContacts(contactsRes.data);
      setRules(rulesRes.data);
      setLogs(logsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load notification settings');
    }
  }, [tenantId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSMTP = async () => {
    if (!tenantId) return;
    try {
      await api.put(`/api/tenants/${tenantId}/notifications/smtp`, {
        host: smtpForm.host, port: parseInt(smtpForm.port),
        username: smtpForm.username || null, password: smtpForm.password || null,
        use_tls: smtpForm.use_tls, from_email: smtpForm.from_email,
        from_name: smtpForm.from_name, is_active: smtpForm.is_active,
      });
      setSuccess('SMTP settings saved');
      setTimeout(() => setSuccess(null), 3000);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const addContact = async () => {
    if (!tenantId) return;
    try {
      await api.post(`/api/tenants/${tenantId}/notifications/contacts`, {
        name: contactName, email: contactEmail, escalation_tier: parseInt(contactTier),
      });
      setContactDialog(false);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Add failed');
    }
  };

  const deleteContact = async (id: number) => {
    if (!tenantId || !window.confirm('Remove this contact?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/notifications/contacts/${id}`);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const addRule = async () => {
    if (!tenantId) return;
    try {
      await api.post(`/api/tenants/${tenantId}/notifications/rules`, {
        name: ruleForm.name, event_type: ruleForm.event_type,
        threshold_seconds: parseInt(ruleForm.threshold_seconds),
        escalation_tier: parseInt(ruleForm.escalation_tier),
        send_recovery: ruleForm.send_recovery, digest_mode: ruleForm.digest_mode,
      });
      setRuleDialog(false);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Add failed');
    }
  };

  const deleteRule = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this rule?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/notifications/rules/${id}`);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const updateSmtpField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setSmtpForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Typography variant="h4" gutterBottom>Notifications</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="SMTP" />
        <Tab label="Contacts" />
        <Tab label="Rules" />
        <Tab label="Log" />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>SMTP Configuration</Typography>
          <TextField label="SMTP Host" fullWidth margin="dense" value={smtpForm.host} onChange={updateSmtpField('host')} />
          <TextField label="Port" fullWidth margin="dense" value={smtpForm.port} onChange={updateSmtpField('port')} type="number" />
          <TextField label="Username" fullWidth margin="dense" value={smtpForm.username} onChange={updateSmtpField('username')} />
          <TextField label="Password" fullWidth margin="dense" value={smtpForm.password} onChange={updateSmtpField('password')} type="password"
            helperText="Leave blank to keep existing password" />
          <FormControlLabel control={<Switch checked={smtpForm.use_tls} onChange={e => setSmtpForm(prev => ({ ...prev, use_tls: e.target.checked }))} />} label="Use TLS" />
          <TextField label="From Email" fullWidth margin="dense" value={smtpForm.from_email} onChange={updateSmtpField('from_email')} />
          <TextField label="From Name" fullWidth margin="dense" value={smtpForm.from_name} onChange={updateSmtpField('from_name')} />
          <FormControlLabel control={<Switch checked={smtpForm.is_active} onChange={e => setSmtpForm(prev => ({ ...prev, is_active: e.target.checked }))} />} label="Active" />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<Save />} onClick={saveSMTP}>Save SMTP</Button>
          </Box>
        </Paper>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setContactName(''); setContactEmail(''); setContactTier('1'); setContactDialog(true); }}>
              Add Contact
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Escalation Tier</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No contacts configured</TableCell></TableRow>
                ) : contacts.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>Tier {c.escalation_tier}</TableCell>
                    <TableCell>{c.is_active ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => deleteContact(c.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={contactDialog} onClose={() => setContactDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add Notification Contact</DialogTitle>
            <DialogContent>
              <TextField label="Name" fullWidth margin="dense" value={contactName} onChange={e => setContactName(e.target.value)} />
              <TextField label="Email" fullWidth margin="dense" value={contactEmail} onChange={e => setContactEmail(e.target.value)} type="email" />
              <TextField label="Escalation Tier" fullWidth margin="dense" value={contactTier} onChange={e => setContactTier(e.target.value)} type="number"
                helperText="1 = first notification, 2 = escalation, 3 = critical" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setContactDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={addContact} disabled={!contactName || !contactEmail}>Add</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tab === 2 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setRuleForm({ name: '', event_type: 'agent_offline', threshold_seconds: '300', escalation_tier: '1', send_recovery: true, digest_mode: false });
              setRuleDialog(true);
            }}>Add Rule</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Threshold</TableCell>
                  <TableCell>Escalation Tier</TableCell>
                  <TableCell>Recovery</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No notification rules</TableCell></TableRow>
                ) : rules.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.event_type}</TableCell>
                    <TableCell>{Math.round(r.threshold_seconds / 60)} min</TableCell>
                    <TableCell>Tier {r.escalation_tier}</TableCell>
                    <TableCell>{r.send_recovery ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => deleteRule(r.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add Notification Rule</DialogTitle>
            <DialogContent>
              <TextField label="Rule Name" fullWidth margin="dense" value={ruleForm.name}
                onChange={e => setRuleForm(prev => ({ ...prev, name: e.target.value }))} />
              <TextField label="Event Type" fullWidth margin="dense" value={ruleForm.event_type}
                onChange={e => setRuleForm(prev => ({ ...prev, event_type: e.target.value }))}
                helperText="e.g., agent_offline, alert_failed" />
              <TextField label="Threshold (seconds)" fullWidth margin="dense" value={ruleForm.threshold_seconds} type="number"
                onChange={e => setRuleForm(prev => ({ ...prev, threshold_seconds: e.target.value }))}
                helperText="Time before notification is sent" />
              <TextField label="Escalation Tier" fullWidth margin="dense" value={ruleForm.escalation_tier} type="number"
                onChange={e => setRuleForm(prev => ({ ...prev, escalation_tier: e.target.value }))} />
              <FormControlLabel control={<Switch checked={ruleForm.send_recovery}
                onChange={e => setRuleForm(prev => ({ ...prev, send_recovery: e.target.checked }))} />} label="Send Recovery Notification" />
              <FormControlLabel control={<Switch checked={ruleForm.digest_mode}
                onChange={e => setRuleForm(prev => ({ ...prev, digest_mode: e.target.checked }))} />} label="Digest Mode" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRuleDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={addRule} disabled={!ruleForm.name}>Add</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tab === 3 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center">No notifications sent</TableCell></TableRow>
              ) : logs.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{new Date(l.created_at).toLocaleString()}</TableCell>
                  <TableCell>{l.event_type}</TableCell>
                  <TableCell>{l.recipient}</TableCell>
                  <TableCell>{l.subject}</TableCell>
                  <TableCell>{l.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
